// components/FileUpload.js
import React, { useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_MIME = new Set(["application/pdf", "image/jpeg", "image/png"]);
const ALLOWED_EXT = new Set([".pdf", ".jpeg", ".jpg", ".png"]);

// Build a strict accept string for the input element
const ACCEPT = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    ".pdf",
    ".jpeg",
    ".jpg",
    ".png",
].join(",");

function getExt(name) {
    const idx = name.lastIndexOf(".");
    return idx === -1 ? "" : name.slice(idx).toLowerCase();
}

function validateFile(file) {
    if (!file) return { ok: false, reason: "No file selected." };
    if (file.size > MAX_BYTES) return { ok: false, reason: "File is larger than 5 MB." };
    const extOk = ALLOWED_EXT.has(getExt(file.name));
    const mimeOk = ALLOWED_MIME.has(file.type);
    if (!extOk || !mimeOk) return { ok: false, reason: "Only PDF, JPEG, or PNG files are allowed." };
    return { ok: true };
}

function toKB(bytes) {
    return (bytes / 1024).toFixed(0);
}

export default function FileUpload({
                                       label = "Upload File",
                                       multiple = false,
                                       onFilesChange,     // (filesArray) => void
                                       initialFiles = [], // optional array of File-like objects to display
                                       buttonSx,          // optional MUI sx for the button
                                   }) {
    const inputRef = useRef(null);
    const [error, setError] = useState("");
    const [files, setFiles] = useState(initialFiles);

    const handlePick = (e) => {
        setError("");
        const selected = Array.from(e.target.files || []);

        // Validate all
        const bad = selected.find((f) => !validateFile(f).ok);
        if (bad) {
            setError(validateFile(bad).reason);
            if (inputRef.current) inputRef.current.value = "";
            return;
        }

        const next = multiple ? selected : selected.slice(0, 1);
        setFiles(next);
        onFilesChange?.(next);
    };

    const clearFiles = () => {
        setFiles([]);
        setError("");
        if (inputRef.current) inputRef.current.value = "";
        onFilesChange?.([]);
    };

    return (
        <Box>
            <Box display="flex" gap={1} alignItems="center">
                <Button variant="contained" component="label" sx={buttonSx}>
                    {label}
                    <input
                        ref={inputRef}
                        hidden
                        type="file"
                        accept={ACCEPT}
                        multiple={multiple}
                        onChange={handlePick}
                    />
                </Button>
                {files.length > 0 && (
                    <Button variant="outlined" color="error" onClick={clearFiles}>
                        Clear
                    </Button>
                )}
            </Box>

            {files.length > 0 && (
                <Box mt={1}>
                    {files.map((f, i) => (
                        <Typography key={i} variant="body2">
                            {f.name} — {toKB(f.size)} KB
                        </Typography>
                    ))}
                </Box>
            )}

            {error && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
}